'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface NavItem {
  id: number
  top_id: number | null
  platform: string
  name: string
  desc?: string
  url: string
  path?: string
  icon: string
  sort: number
  nav: number
}

interface Platform {
  platform: string
}

interface User {
  id: number
  email: string
  phone?: string
  name: string
  role: string
  admin: number
  is_support_team?: number
  is_manager?: number
  is_developer?: number
  firm_id: number | null
  firm_store_id: number | null
  platforms: Platform[]
  nav: NavItem[]
  mod: any[]
  store: any[]
  firmStores: any[]
  firm: any
  nav_scope?: any
}

interface UserContextType {
  user: User | null
  loading: boolean
  error: string | null
  refreshUser: () => Promise<void>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

function redirectToLoginForInvalidStore() {
  if (typeof window === 'undefined') return
  const pathname = window.location.pathname
  if (
    pathname === '/login' ||
    pathname.startsWith('/login/') ||
    pathname === '/support' ||
    pathname.startsWith('/support/') ||
    pathname === '/embed' ||
    pathname.startsWith('/embed/')
  ) {
    return
  }
  localStorage.removeItem('auth_token')
  localStorage.removeItem('refresh_token')
  window.location.replace('/login?reason=no_store')
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchUser = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const token = localStorage.getItem('auth_token')
      if (!token) {
        console.log('[UserContext] No auth token found')
        setUser(null)
        setLoading(false)
        return
      }

      console.log('[UserContext] Fetching /auth/me...')
      const startTime = Date.now()
      
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10s timeout

      const storeScope = localStorage.getItem('admin_store_nav_scope') === '1' ? '?store_scope=1' : ''
      const response = await fetch(`https://app.canerozen.com/api/auth/me${storeScope}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include', // Include cookies for cross-domain requests
        signal: controller.signal
      })

      clearTimeout(timeoutId)
      const duration = Date.now() - startTime
      console.log(`[UserContext] /auth/me completed in ${duration}ms`)

      if (response.ok) {
        const data = await response.json()
        console.log('[UserContext] User data received:', data.data.user.email)
        console.log('[UserContext] Full data structure:', data.data)
        console.log('[UserContext] Nav items count:', data.data.nav?.length)
        
        // Flatten the structure for easier access
        const flattenedUser = {
          ...data.data.user,
          firmStores: data.data.firmStores,
          nav: data.data.nav || [],
          platforms: data.data.platforms || [],
          nav_scope: data.data.nav_scope
        }

        const firmStores = Array.isArray(flattenedUser.firmStores) ? flattenedUser.firmStores : []
        const hasActiveStore = firmStores.some((store: any) => Number(store.id) === Number(flattenedUser.firm_store_id))
        if ((flattenedUser.firm_id || firmStores.length > 0) && !hasActiveStore) {
          setUser(null)
          setError('Aktif mağaza bulunamadı')
          redirectToLoginForInvalidStore()
          return
        }
        
        console.log('[UserContext] Flattened user nav count:', flattenedUser.nav.length)
        setUser(flattenedUser)
      } else {
        console.log('[UserContext] Response not OK:', response.status)
        const errorData = await response.json().catch(() => ({}))
        console.log('[UserContext] Error response:', errorData)
        // Token geçersiz, temizle
        localStorage.removeItem('auth_token')
        localStorage.removeItem('refresh_token')
        setUser(null)
        if (errorData?.code === 'NO_ACTIVE_STORE') {
          redirectToLoginForInvalidStore()
        }
      }
    } catch (err) {
      console.error('[UserContext] Failed to fetch user:', err)
      if (err instanceof Error && err.name === 'AbortError') {
        setError('İstek zaman aşımına uğradı (10s)')
      } else {
        setError('Kullanıcı bilgileri alınamadı')
      }
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const loadingGuard = window.setTimeout(() => {
      setLoading((current) => {
        if (!current) return current
        console.warn('[UserContext] Loading guard released a stuck auth state')
        setError('Kullanıcı oturumu zaman aşımına uğradı')
        setUser(null)
        return false
      })
    }, 12000)

    fetchUser()

    return () => window.clearTimeout(loadingGuard)
  }, [])

  return (
    <UserContext.Provider value={{ user, loading, error, refreshUser: fetchUser }}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
